<template>
  <v-container>
    <v-row class="fill-height" align="center" justify="center">
      <!-- show branch cards if any, otherwise show no-access card -->
      <template v-if="filteredBranch && filteredBranch.length">
        <template v-for="(item, i) in filteredBranch">
          <v-col :key="i" align="center">
            <v-hover v-slot="{ hover }">
              <div class="card-wrapper">
                <v-card
                  width="300px"
                  shaped
                  @click="viewItem(item.id)"
                  :elevation="hover || item.id === highlightedBranchId ? 12 : 2"
                  :class="{
                    'on-hover': hover,
                    highlighted: item.id === highlightedBranchId,
                    'gradient-bg': true,
                  }"
                >
                  <v-card-title class="text-h4 white--text">
                    <v-row
                      class="fill-height flex-column"
                      justify="space-between"
                    >
                      <p class="mt-4 subheading font-weight-bold">
                        {{ item.brCode }}
                      </p>

                      <div>
                        <p class="text-caption font-weight-medium font-italic">
                          {{ item.name }}
                        </p>
                      </div>

                      <div class="mt-2 d-flex align-center justify-center flex-wrap" style="gap: 4px;">
                        <v-chip small color="success" class="white--text">
                          <v-icon left small>mdi-play-circle-outline</v-icon>
                          {{ getActiveCountForBranch(item.id) }} Active
                        </v-chip>
                        <!-- Archived chip commented out for now
                        <v-chip small color="grey darken-1" class="white--text">
                          <v-icon left small>mdi-archive-outline</v-icon>
                          {{ getArchivedCountForBranch(item.id) }} Archived
                        </v-chip>
                        -->
                      </div>
                    </v-row>
                  </v-card-title>
                </v-card>
              </div>
            </v-hover>
          </v-col>
        </template>
      </template>

      <template v-else>
        <v-col cols="12" class="text-center">
          <v-card
            width="320px"
            shaped
            elevation="2"
            class="no-access-card mx-auto pa-4"
          >
            <v-card-title class="text-h6 d-flex align-center justify-center">
              <v-icon left color="primary" class="mr-2">mdi-lock</v-icon>
              No Branch Access
            </v-card-title>
            <v-card-text>
              You currently do not have access to any branches. Please contact
              your system administrator to request access.
            </v-card-text>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import BranchDataService from "../../services/BranchDataService";
import CasefileDataService from "../../services/CasefileDataService";
export default {
  data() {
    return {
      branch: [],
      highlightedBranchId: null,
      countsByBranch: {},
      activeCountsByBranch: {},
      archivedCountsByBranch: {},
      loadingCounts: false,
    };
  },
  mounted() {
    this.retrieveBr();
    this.retrieveCounts();

    // Check if a branch ID was passed in the query
    if (this.$route.query.branch) {
      this.highlightedBranchId = parseInt(this.$route.query.branch, 10);

      // Remove the query parameter after using it
      this.$router.replace({ path: this.$route.path });
    }
  },
  computed: {
    filteredBranch() {
      // Defensive handling if user or roles/branches missing
      const user = this.$store.state.auth.user || {};
      const roles = Array.isArray(user.roles) ? user.roles : [];
      const isAdminOrManager =
        roles.includes("ADMIN") || roles.includes("MANAGER");
      if (!isAdminOrManager) {
        const userBranches = Array.isArray(user.branches) ? user.branches : [];
        return this.branch.filter((x) => userBranches.includes(x.id));
      } else return this.branch;
    },
  },
  methods: {
    retrieveBr() {
      BranchDataService.getAll()
        .then((response) => {
          this.branch = response.data;
        })
        .catch((e) => {
          console.error(e);
        });
    },

    async retrieveCounts() {
      this.loadingCounts = true;
      try {
        const user = this.$store.state.auth.user || {};
        const params = {
          roles: user.roles,
          dept: user.dept,
          branches: user.branches,
          userId: user.id,
        };
        console.log('[Branch Tab] Fetching counts with params:', params);

        // Fetch total counts
        const totalResp = await CasefileDataService.getCountsBy('branch', params);
        if (totalResp.data && totalResp.data.counts) {
          this.countsByBranch = totalResp.data.counts;
        }

        // Fetch Active/Archived breakdown
        const statusResp = await CasefileDataService.getCountsByStatus('branch', params);
        if (statusResp.data && statusResp.data.counts) {
          this.activeCountsByBranch = statusResp.data.counts.active || {};
          this.archivedCountsByBranch = statusResp.data.counts.archived || {};
        }
      } catch (e) {
        console.error('[Branch Tab] Counts fetch failed:', e);
      } finally {
        this.loadingCounts = false;
      }
    },

    getCountForBranch(branchId) {
      const key = String(branchId);
      return this.countsByBranch[key] || this.countsByBranch[branchId] || 0;
    },

    getActiveCountForBranch(branchId) {
      const key = String(branchId);
      return this.activeCountsByBranch[key] || this.activeCountsByBranch[branchId] || 0;
    },

    getArchivedCountForBranch(branchId) {
      const key = String(branchId);
      return this.archivedCountsByBranch[key] || this.archivedCountsByBranch[branchId] || 0;
    },

    viewItem(id) {
      this.$router.push({ name: "brTabDetail", params: { id: id } });
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: all 0.4s ease-in-out;
}

.gradient-bg {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  position: relative;
  overflow: hidden;
}

.v-card:not(.on-hover):not(.highlighted) {
  opacity: 0.6;
}

.highlighted {
  box-shadow: 0 0 15px rgba(0, 128, 0, 0.5);
  opacity: 1;
}

.card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* No-access card styling (matches department tab style) */
.no-access-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%);
  border: 1px dashed rgba(0, 0, 0, 0.06);
  color: #37474f;
  max-width: 360px;
}
</style>
